'use client';

import type { FormAnswers, FormCampusOption, WebForm } from './types';
import { childrenOf, isCampusField, isVisible, withAnswer } from './logic';
import { FormField } from './FormField';

/**
 * A whole form: top-level fields in MP's order, conditional ones folding in
 * as their gates are answered, a group's children under their parent.
 * Answers are the flat `FormAnswers` map every server route validates.
 *
 * Each question carries `data-field-id`, which is how a host scrolls to the
 * first unanswered required question instead of printing a list of names and
 * leaving somebody to hunt for them.
 */
export function FormFields({
  form,
  answers,
  onChange,
  campuses,
  className = 'space-y-5',
  invalidIds,
  aboutName = null,
  aboutGender = null,
  personGender = null,
  onPersonGender,
}: {
  form: WebForm;
  answers: FormAnswers;
  onChange: (next: FormAnswers) => void;
  campuses?: FormCampusOption[];
  className?: string;
  /** Required fields still unanswered, once somebody has tried to send. */
  invalidIds?: ReadonlySet<number>;
  /** A first name when these answers are about somebody other than the reader. */
  aboutName?: string | null;
  /** What MP holds, so a pronoun can fit. Unknown means they/them. */
  aboutGender?: 'male' | 'female' | null;
  /** Their gender for choosing, even when the reader is answering for themselves. */
  personGender?: 'male' | 'female' | null;
  onPersonGender?: (gender: 'male' | 'female') => void;
}) {
  const setAnswer = (id: number, value: string) => onChange(withAnswer(answers, id, value));
  const campusNames = (campuses ?? []).map((c) => c.name);
  return (
    <div className={className}>
      {form.fields.map((field) => {
        if (field.parentId !== null || !isVisible(form, field, answers)) return null;
        return (
          <div key={field.id} data-field-id={field.id} className="scroll-mt-24">
            <FormField
              field={field}
              children={childrenOf(form, field)}
              answers={answers}
              setAnswer={setAnswer}
              campuses={campuses}
              asCampus={campusNames.length > 0 && isCampusField(field, campusNames)}
              invalid={invalidIds?.has(field.id)}
              aboutName={aboutName}
              aboutGender={aboutGender}
              personGender={personGender}
              onPersonGender={onPersonGender}
            />
          </div>
        );
      })}
    </div>
  );
}
